import { type FC, useCallback, useMemo } from 'react';

import { Icon, type IconProps } from '@vakantiesnl/components/src/atoms/Icon';
import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { type DestinationType } from '@vakantiesnl/types/src/elasticsearch/entities';

import { useCustomStyles } from './AccommodationType.style';

type AccoTypeProps = {
	/** String containing the acco type */
	accoType: string;
	/** Link to pass on to the acco type */
	link?: string;
	/** Show specific icon if needed */
	icon?: IconProps['type'];
	/** Tracking event for link clicks */
	handleLinkClick?: (
		destinationUrl: string,
		destinationType?: DestinationType,
		isAccoHeader?: boolean,
		atomType?: string,
	) => void;
	/** Custom className to pass */
	className?: string;
	/** Size for the icon */
	iconSize?: IconProps['size'];
};

export const AccommodationType: FC<AccoTypeProps> = ({
	accoType,
	link,
	icon,
	handleLinkClick,
	className,
	iconSize,
}) => {
	const { classes, cx } = useCustomStyles();
	const microCopies = useMicroCopyContext();

	const linkOnClick = useCallback(() => {
		if (link) handleLinkClick?.(link, undefined, true, 'acco_type');
	}, [handleLinkClick, link]);

	const accoTypeIcon: IconProps['type'] = useMemo(() => {
		/** Just for safety */
		if (!accoType) {
			return 'accommodation';
		}

		switch (accoType) {
			case microCopies['accoType.chalet']:
				return 'chalet';
			case microCopies['accoType.lodge']:
				return 'chalet';
			case microCopies['accoType.aparthotel']:
				return 'apartHotel';
			case microCopies['accoType.camping']:
				return 'camping';
			case microCopies['accoType.bungalow_tent']:
				return 'camping';
			case microCopies['accoType.castle']:
				return 'castle';
			case microCopies['accoType.hostel']:
				return 'hostel';
			case microCopies['accoType.hotel']:
				return 'hotel';
			case microCopies['accoType.resort']:
				return 'holidayPark';
			case microCopies['accoType.bed_and_breakfast']:
				return 'bedBreakfast';
			case microCopies['accoType.country_house']:
				return 'mansion';
			case microCopies['accoType.villa']:
				return 'villa';
			case microCopies['accoType.vakation_house']:
				return 'home';
			default:
				return 'accommodation';
		}
	}, [accoType, microCopies]);

	return link ? (
		<Link href={link} className={cx(classes.text, className)} onClick={linkOnClick} icon={accoTypeIcon}>
			{accoType}
		</Link>
	) : (
		<div className={cx(classes.accoTypeContainer, className)}>
			{icon && <Icon type={accoTypeIcon} size={iconSize} className={classes.icon} />}
			<Typography variant="labelSm" as="span" className={classes.text}>
				{accoType}
			</Typography>
		</div>
	);
};
